import { useRef, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowsHorizontal, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, scrollToId } from "@/components/Reveal";

const BeforeAfter = ({ beforeSrc, afterSrc, beforeLabel, afterLabel, hint }) => {
  const containerRef = useRef(null);
  const [pct, setPct] = useState(52);
  const [dragging, setDragging] = useState(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const raw = ((clientX - rect.left) / rect.width) * 100;
    setPct(Math.max(4, Math.min(96, raw)));
  }, []);

  const onPointerDown = (e) => {
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    setFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };
  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPct((p) => Math.max(4, p - 3));
    if (e.key === "ArrowRight") setPct((p) => Math.min(96, p + 3));
  };

  useEffect(() => {
    if (!dragging) return;
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.userSelect = "";
    };
  }, [dragging]);

  return (
    <div
      ref={containerRef}
      data-testid="before-after"
      className={`relative w-full aspect-[4/5] md:aspect-[16/11] overflow-hidden bg-[#EDE6DA] select-none ${
        dragging ? "cursor-grabbing" : "cursor-grab"
      }`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* AFTER (render) is the base layer — full image visible */}
      <img
        src={afterSrc}
        alt={afterLabel}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      {/* BEFORE (real photo) on top, clipped from right */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
      >
        <img
          src={beforeSrc}
          alt={beforeLabel}
          draggable={false}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 text-[10px] tracking-luxe uppercase text-white bg-[#1A1A1A]/70 px-2.5 py-1 pointer-events-none">
        {beforeLabel}
      </div>
      <div className="absolute top-4 right-4 text-[10px] tracking-luxe uppercase text-[#1A1A1A] bg-white/85 px-2.5 py-1 pointer-events-none">
        {afterLabel}
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white/90 shadow-[0_0_24px_rgba(0,0,0,0.35)] pointer-events-none"
        style={{ left: `${pct}%` }}
      />

      {/* Handle */}
      <button
        type="button"
        aria-label="Drag to compare"
        onKeyDown={onKey}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-white/95 backdrop-blur border border-black/10 shadow-xl flex items-center justify-center text-[#1A1A1A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355]"
        style={{ left: `${pct}%` }}
        tabIndex={0}
      >
        <ArrowsHorizontal size={20} weight="bold" />
      </button>

      {/* Hint (fades on interact) */}
      <div
        className={`absolute bottom-4 left-1/2 -translate-x-1/2 text-[10px] tracking-luxe uppercase text-white bg-black/45 backdrop-blur-sm px-3 py-1.5 transition-opacity duration-500 pointer-events-none ${
          dragging || pct !== 52 ? "opacity-0" : "opacity-100"
        }`}
      >
        ← {hint} →
      </div>
    </div>
  );
};

export const TurnkeyStore = () => {
  const { t } = useLang();

  return (
    <section
      id="format"
      data-testid="turnkey"
      className="relative bg-[#F3EFEA] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
          {/* Left: before/after image */}
          <Reveal>
            <BeforeAfter
              beforeSrc="/homezen-shelving-render.jpg"
              afterSrc="/homezen-shelving-real.webp"
              beforeLabel={t.turnkey.before}
              afterLabel={t.turnkey.after}
              hint={t.turnkey.dragHint}
            />
          </Reveal>

          {/* Right: content */}
          <div>
            <Reveal>
              <p className="text-xs tracking-luxe uppercase text-[#8B7355] mb-4">
                {t.turnkey.kicker}
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] mb-6">
                {t.turnkey.title}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-lg text-[#1A1A1A]/75 leading-relaxed mb-10 max-w-xl">
                {t.turnkey.subtitle}
              </p>
            </Reveal>

            <motion.ul
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-70px" }}
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.08 } },
              }}
              className="space-y-5 mb-10"
              data-testid="turnkey-bullets"
            >
              {t.turnkey.bullets.map((b, i) => (
                <motion.li
                  key={i}
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
                  }}
                  className="flex gap-4"
                >
                  <CheckCircle
                    size={22}
                    weight="thin"
                    className="text-[#8B7355] shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="font-display text-xl leading-tight">{b.title}</p>
                    <p className="text-sm text-[#1A1A1A]/70 leading-relaxed mt-1">{b.text}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>

            <Reveal delay={0.2}>
              <button
                type="button"
                data-testid="turnkey-cta"
                onClick={() => scrollToId("contact")}
                className="group inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FAFAF7] text-sm px-7 py-4 hover:bg-[#8B7355] transition-colors"
              >
                {t.turnkey.cta}
                <ArrowRight
                  size={16}
                  weight="light"
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
